import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios'
import { tokenService } from '@/services/auth/token.service'
import { API_CONFIG } from '@/config/api'

// 注入的 AuthService 实例，避免循环依赖
let _authService: any = null

export const setAuthService = (service: any) => {
  _authService = service
}

// 从 Cookie 中获取值
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }
  return null
}

// 初始化标志
let isInitializing = false
let initPromise: Promise<void> | null = null

// 初始化 XSRF Token（调用 application-configuration 让服务器设置 Cookie）
// force: 是否强制刷新，忽略缓存
async function initXsrfToken(force: boolean = false): Promise<void> {
  // 如果强制刷新，先清空 Cookie 中的旧 Token
  if (force) {
    document.cookie = 'XSRF-TOKEN=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    isInitializing = false
    initPromise = null
  }

  if (isInitializing && !force) {
    return initPromise!
  }

  isInitializing = true
  initPromise = (async () => {
    try {
      // 获取当前用户的 access token
      const token = tokenService.getAccessToken()

      const headers: Record<string, string> = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // 调用 application-configuration 端点，服务器会在响应中设置 XSRF-TOKEN cookie
      await axios.get(`${API_CONFIG.baseURL}/api/abp/application-configuration`, {
        withCredentials: true, // 确保接收和发送 cookies
        headers
      })
      console.log('[HTTP] XSRF-TOKEN initialized from application-configuration')
    } catch (error) {
      console.error('[HTTP] Failed to initialize XSRF-TOKEN:', error)
    } finally {
      isInitializing = false
    }
  })()

  return initPromise
}

// 获取 XSRF Token，如果不存在则初始化
async function getXsrfToken(): Promise<string | null> {
  let token = getCookie('XSRF-TOKEN')

  // 如果 token 不存在，调用 application-configuration 初始化
  if (!token) {
    await initXsrfToken()
    token = getCookie('XSRF-TOKEN')
  }

  return token
}

// 处理认证失效的重定向
const handleAuthFailure = (_errorMessage: string = '登录已过期，请重新登录') => {
  console.warn('Authentication failed, clearing tokens and redirecting to login')
  if (_authService) {
    _authService.logout()
  } else {
    tokenService.clearTokens()
  }
  
  // 获取当前路径作为重定向参数
  const currentPath = window.location.pathname + window.location.search
  const loginUrl = `/login${currentPath !== '/' ? `?redirect=${encodeURIComponent(currentPath)}` : ''}`
  
  // 使用 location.href 进行重定向，确保清除所有状态
  window.location.href = loginUrl
}

// 创建axios实例
const http: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  withCredentials: true, // 启用 Cookie 支持
  paramsSerializer: {
    // 不使用方括号格式：investmentRating=5 而不是 investmentRating[]=5
    indexes: null
  },
  headers: {
    'Content-Type': 'application/json',
    // 添加 X-Requested-With 头告诉服务器这是 AJAX 请求，ABP 会返回 401 而不是重定向
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// 请求拦截器
http.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 获取访问令牌
    const token = tokenService.getAccessToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 为 POST, PUT, DELETE 请求添加 XSRF Token
    const method = config.method?.toUpperCase()
    if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
      const xsrfToken = await getXsrfToken()  // 确保 await
      if (xsrfToken) {
        config.headers['RequestVerificationToken'] = xsrfToken
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // 如果是401错误且不是refresh token请求，尝试刷新token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        if (_authService) {
          const refreshedUser = await _authService.refreshToken()

          if (refreshedUser) {
            // 更新请求头
            originalRequest.headers.Authorization = `Bearer ${refreshedUser.access_token}`

            // 重新发送原始请求
            return http(originalRequest)
          }
        }

        // 刷新失败或服务不可用，清除认证状态并跳转到登录页
        handleAuthFailure('Token刷新失败，请重新登录')
      } catch (refreshError) {
        // 刷新失败，清除认证状态并跳转到登录页
        handleAuthFailure('Token刷新异常，请重新登录')
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default http

// 导出初始化函数供应用启动时调用
export { initXsrfToken }